import { useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const VIBES = ["Clean & Modern", "Bold & Aggressive", "Friendly & Community", "Professional & Premium"];
const AI_TONES = ["Friendly & Casual", "Motivating & Energetic", "Professional & Formal"];

function Field({ label, required, children, hint }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontWeight: 700, color: "#0F172A", fontSize: 14, marginBottom: 4 }}>
        {label}{required && <span style={{ color: "#DC2626" }}> *</span>}
      </label>
      {hint && <p style={{ fontSize: 12, color: "#64748B", margin: "0 0 6px" }}>{hint}</p>}
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", border: "1.5px solid #CBD5E1", borderRadius: 8,
  padding: "10px 12px", fontSize: 14, color: "#0F172A",
  outline: "none", background: "#F8FAFC", boxSizing: "border-box",
};

const sectionStyle = {
  background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 14,
  padding: "20px 20px", marginBottom: 20,
};

export default function GymBrandingPage() {
  const [form, setForm] = useState({
    gym_name: "", tagline: "", gym_type: "", owner_first: "", owner_last: "",
    owner_email: "", address: "", city: "", state: "", zip: "",
    primary_color: "", secondary_color: "", logo_url: "", vibe: "",
    class_types: "", class_duration: "", class_capacity: "", class_schedule: "",
    twilio_sid: "", twilio_token: "", twilio_phone: "", admin_notify_phone: "", sms_tone: "",
    stripe_publishable: "", stripe_secret: "",
    instagram: "", facebook: "", tiktok: "", google_biz: "",
    extra_notes: "",
  });
  const [introOffers, setIntroOffers]     = useState([{ name: "", price: "", description: "" }]);
  const [plans, setPlans]                 = useState([{ name: "", price: "", classes: "", description: "" }]);
  const [trainers, setTrainers]           = useState([{ first_name: "", last_name: "", specialty: "" }]);
  const [submitting, setSubmitting]       = useState(false);
  const [done, setDone]                   = useState(false);
  const [error, setError]                 = useState("");

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Dynamic row helpers
  const addRow    = (setter, blank)       => setter(r => [...r, blank]);
  const removeRow = (setter, i)           => setter(r => r.filter((_, j) => j !== i));
  const setRow    = (setter, i, k, v)     => setter(r => r.map((row, j) => j === i ? { ...row, [k]: v } : row));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.gym_name || !form.owner_first || !form.owner_email) {
      setError("Gym Name, Owner First Name, and Email are required.");
      return;
    }
    setError(""); setSubmitting(true);
    try {
      await axios.post(`${API}/gympartner/branding`, {
        ...form, intro_offers: introOffers, membership_plans: plans, trainers,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    }
    setSubmitting(false);
  };

  if (done) return (
    <div style={{ minHeight: "100vh", background: "#F0FDFF", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Poppins', sans-serif" }}>
      <div style={{ background: "#fff", borderRadius: 20, maxWidth: 520, width: "100%", padding: "40px 32px", textAlign: "center", boxShadow: "0 8px 40px rgba(8,145,178,0.1)" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
        <h2 style={{ fontSize: 24, fontWeight: 900, color: "#0C2D48", marginBottom: 8, fontFamily: "inherit", textTransform: "none", letterSpacing: "normal" }}>Questionnaire Submitted!</h2>
        <p style={{ color: "#475569", fontSize: 15, lineHeight: 1.6 }}>
          Thank you! Ron K. will review your information and reach out with next steps for setting up your gym's platform.
        </p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#F0FDFF", padding: "32px 16px", fontFamily: "'Poppins', -apple-system, sans-serif" }}>
      <div style={{ maxWidth: 680, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontWeight: 900, fontSize: 22, color: "#0891B2", marginBottom: 6 }}>GymPartnerAI</div>
          <h1 style={{ fontSize: "clamp(22px, 5vw, 30px)", fontWeight: 900, color: "#0C2D48", margin: "0 0 10px", fontFamily: "inherit", textTransform: "none", letterSpacing: "normal" }}>
            Gym Branding Questionnaire
          </h1>
          <p style={{ color: "#475569", fontSize: 14, maxWidth: 460, margin: "0 auto" }}>
            Fill this out completely so we can set up and brand your gym's platform. Every field helps us build it exactly the way you need it.
          </p>
        </div>

        <form onSubmit={handleSubmit}>

          {/* ── Section 1: Gym Identity ── */}
          <div style={sectionStyle}>
            <h2 style={{ fontSize: 16, fontWeight: 900, color: "#0891B2", margin: "0 0 16px", fontFamily: "inherit", textTransform: "none", letterSpacing: "normal" }}>1. Gym Identity</h2>
            <Field label="Gym Name" required><input style={inputStyle} value={form.gym_name} onChange={set("gym_name")} placeholder="e.g. Iron Forge Fitness" /></Field>
            <Field label="Tagline / Slogan" hint="The short phrase under your gym name"><input style={inputStyle} value={form.tagline} onChange={set("tagline")} placeholder="e.g. Where Champions Are Made" /></Field>
            <Field label="Gym Type" required hint="e.g. Boxing, CrossFit, MMA, Yoga, General Fitness"><input style={inputStyle} value={form.gym_type} onChange={set("gym_type")} placeholder="e.g. Boxing & Fitness" /></Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Owner First Name" required><input style={inputStyle} value={form.owner_first} onChange={set("owner_first")} placeholder="First Name" /></Field>
              <Field label="Owner Last Name"><input style={inputStyle} value={form.owner_last} onChange={set("owner_last")} placeholder="Last Name" /></Field>
            </div>
            <Field label="Owner / Business Email" required><input style={inputStyle} type="email" value={form.owner_email} onChange={set("owner_email")} placeholder="you@yourgym.com" /></Field>
            <Field label="Street Address"><input style={inputStyle} value={form.address} onChange={set("address")} placeholder="1234 Main St" /></Field>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
              <Field label="City"><input style={inputStyle} value={form.city} onChange={set("city")} placeholder="Columbus" /></Field>
              <Field label="State"><input style={inputStyle} value={form.state} onChange={set("state")} placeholder="OH" /></Field>
              <Field label="Zip"><input style={inputStyle} value={form.zip} onChange={set("zip")} placeholder="43214" /></Field>
            </div>
          </div>

          {/* ── Section 2: Brand & Design ── */}
          <div style={sectionStyle}>
            <h2 style={{ fontSize: 16, fontWeight: 900, color: "#0891B2", margin: "0 0 16px", fontFamily: "inherit", textTransform: "none", letterSpacing: "normal" }}>2. Brand & Design</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Primary Brand Color" hint="Hex code or color name"><input style={inputStyle} value={form.primary_color} onChange={set("primary_color")} placeholder="#FF6B35 or Orange" /></Field>
              <Field label="Secondary Brand Color"><input style={inputStyle} value={form.secondary_color} onChange={set("secondary_color")} placeholder="#1E293B or Dark Navy" /></Field>
            </div>
            <Field label="Logo URL" hint="Google Drive, Dropbox, or direct image link"><input style={inputStyle} value={form.logo_url} onChange={set("logo_url")} placeholder="https://drive.google.com/..." /></Field>
            <Field label="Overall Style Vibe">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {VIBES.map(v => (
                  <button key={v} type="button" onClick={() => setForm(f => ({ ...f, vibe: v }))}
                    style={{ padding: "10px 12px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", border: "1.5px solid", transition: "all 0.15s",
                      borderColor: form.vibe === v ? "#0891B2" : "#CBD5E1",
                      background: form.vibe === v ? "#EFF6FF" : "#fff",
                      color: form.vibe === v ? "#0891B2" : "#475569",
                    }}>{v}</button>
                ))}
              </div>
            </Field>
          </div>

          {/* ── Section 3: Intro Offers (unlimited) ── */}
          <div style={sectionStyle}>
            <h2 style={{ fontSize: 16, fontWeight: 900, color: "#0891B2", margin: "0 0 4px", fontFamily: "inherit", textTransform: "none", letterSpacing: "normal" }}>3. Intro Offers</h2>
            <p style={{ fontSize: 12, color: "#64748B", marginBottom: 14 }}>Add as many intro offers as you use. e.g. "First Class Free", "Free 1 Week Pass", "$1 First Month"</p>
            {introOffers.map((o, i) => (
              <div key={i} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "14px 14px", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: "#0891B2" }}>Offer {i + 1}</span>
                  {introOffers.length > 1 && <button type="button" onClick={() => removeRow(setIntroOffers, i)} style={{ background: "none", border: "none", color: "#DC2626", fontSize: 12, cursor: "pointer", fontWeight: 700 }}>Remove</button>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, marginBottom: 8 }}>
                  <input style={inputStyle} placeholder="Offer Name (e.g. First Class Free)" value={o.name} onChange={e => setRow(setIntroOffers, i, "name", e.target.value)} />
                  <input style={inputStyle} placeholder="Price (e.g. $0)" value={o.price} onChange={e => setRow(setIntroOffers, i, "price", e.target.value)} />
                </div>
                <input style={inputStyle} placeholder="Short description (optional)" value={o.description} onChange={e => setRow(setIntroOffers, i, "description", e.target.value)} />
              </div>
            ))}
            <button type="button" onClick={() => addRow(setIntroOffers, { name: "", price: "", description: "" })}
              style={{ background: "#EFF6FF", border: "1.5px dashed #0891B2", borderRadius: 8, padding: "9px 16px", color: "#0891B2", fontWeight: 700, fontSize: 13, cursor: "pointer", width: "100%" }}>
              + Add Another Intro Offer
            </button>
          </div>

          {/* ── Section 4: Membership Plans (unlimited) ── */}
          <div style={sectionStyle}>
            <h2 style={{ fontSize: 16, fontWeight: 900, color: "#0891B2", margin: "0 0 4px", fontFamily: "inherit", textTransform: "none", letterSpacing: "normal" }}>4. Membership Plans</h2>
            <p style={{ fontSize: 12, color: "#64748B", marginBottom: 14 }}>Add all your membership plan options. No limit.</p>
            {plans.map((p, i) => (
              <div key={i} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "14px 14px", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: "#0891B2" }}>Plan {i + 1}</span>
                  {plans.length > 1 && <button type="button" onClick={() => removeRow(setPlans, i)} style={{ background: "none", border: "none", color: "#DC2626", fontSize: 12, cursor: "pointer", fontWeight: 700 }}>Remove</button>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginBottom: 8 }}>
                  <input style={inputStyle} placeholder="Plan Name (e.g. Starter)" value={p.name} onChange={e => setRow(setPlans, i, "name", e.target.value)} />
                  <input style={inputStyle} placeholder="$/month" value={p.price} onChange={e => setRow(setPlans, i, "price", e.target.value)} />
                  <input style={inputStyle} placeholder="Classes/mo or Unlimited" value={p.classes} onChange={e => setRow(setPlans, i, "classes", e.target.value)} />
                </div>
                <input style={inputStyle} placeholder="Plan description (optional)" value={p.description} onChange={e => setRow(setPlans, i, "description", e.target.value)} />
              </div>
            ))}
            <button type="button" onClick={() => addRow(setPlans, { name: "", price: "", classes: "", description: "" })}
              style={{ background: "#EFF6FF", border: "1.5px dashed #0891B2", borderRadius: 8, padding: "9px 16px", color: "#0891B2", fontWeight: 700, fontSize: 13, cursor: "pointer", width: "100%" }}>
              + Add Another Membership Plan
            </button>
          </div>

          {/* ── Section 5: Classes ── */}
          <div style={sectionStyle}>
            <h2 style={{ fontSize: 16, fontWeight: 900, color: "#0891B2", margin: "0 0 16px", fontFamily: "inherit", textTransform: "none", letterSpacing: "normal" }}>5. Classes</h2>
            <Field label="Types of Classes Offered" hint="List all class types, one per line or comma separated"><textarea style={{ ...inputStyle, resize: "vertical" }} rows={3} value={form.class_types} onChange={set("class_types")} placeholder="e.g. Group Boxing, Personal Training, Beginner Bootcamp" /></Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Class Duration (minutes)"><input style={inputStyle} type="number" value={form.class_duration} onChange={set("class_duration")} placeholder="e.g. 60" /></Field>
              <Field label="Max Capacity per Class"><input style={inputStyle} type="number" value={form.class_capacity} onChange={set("class_capacity")} placeholder="e.g. 42" /></Field>
            </div>
            <Field label="Weekly Schedule" hint="Rough description is fine — the AI will format it"><textarea style={{ ...inputStyle, resize: "vertical" }} rows={3} value={form.class_schedule} onChange={set("class_schedule")} placeholder="e.g. Mon/Wed/Fri 6am, 9am, 5:45pm, 7pm. Sat/Sun 10am. Tuesday 5:45pm only." /></Field>
          </div>

          {/* ── Section 6: Trainers (unlimited) ── */}
          <div style={sectionStyle}>
            <h2 style={{ fontSize: 16, fontWeight: 900, color: "#0891B2", margin: "0 0 4px", fontFamily: "inherit", textTransform: "none", letterSpacing: "normal" }}>6. Trainers</h2>
            <p style={{ fontSize: 12, color: "#64748B", marginBottom: 14 }}>Add all your trainers. No limit.</p>
            {trainers.map((t, i) => (
              <div key={i} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "14px 14px", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: "#0891B2" }}>Trainer {i + 1}</span>
                  {trainers.length > 1 && <button type="button" onClick={() => removeRow(setTrainers, i)} style={{ background: "none", border: "none", color: "#DC2626", fontSize: 12, cursor: "pointer", fontWeight: 700 }}>Remove</button>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 10 }}>
                  <input style={inputStyle} placeholder="First Name" value={t.first_name} onChange={e => setRow(setTrainers, i, "first_name", e.target.value)} />
                  <input style={inputStyle} placeholder="Last Name" value={t.last_name} onChange={e => setRow(setTrainers, i, "last_name", e.target.value)} />
                  <input style={inputStyle} placeholder="Specialty (e.g. Boxing, Strength)" value={t.specialty} onChange={e => setRow(setTrainers, i, "specialty", e.target.value)} />
                </div>
              </div>
            ))}
            <button type="button" onClick={() => addRow(setTrainers, { first_name: "", last_name: "", specialty: "" })}
              style={{ background: "#EFF6FF", border: "1.5px dashed #0891B2", borderRadius: 8, padding: "9px 16px", color: "#0891B2", fontWeight: 700, fontSize: 13, cursor: "pointer", width: "100%" }}>
              + Add Another Trainer
            </button>
          </div>

          {/* ── Section 7: SMS & Twilio ── */}
          <div style={sectionStyle}>
            <h2 style={{ fontSize: 16, fontWeight: 900, color: "#0891B2", margin: "0 0 4px", fontFamily: "inherit", textTransform: "none", letterSpacing: "normal" }}>7. SMS & Twilio</h2>
            <p style={{ fontSize: 12, color: "#64748B", marginBottom: 14 }}>You'll need a Twilio account. If you don't have one yet, leave these blank and we'll help you set it up.</p>
            <Field label="Twilio Account SID" hint="Starts with AC..."><input style={inputStyle} value={form.twilio_sid} onChange={set("twilio_sid")} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" /></Field>
            <Field label="Twilio Auth Token"><input style={inputStyle} value={form.twilio_token} onChange={set("twilio_token")} placeholder="Your Twilio auth token" /></Field>
            <Field label="Twilio Phone Number" hint="The number members will receive texts from"><input style={inputStyle} value={form.twilio_phone} onChange={set("twilio_phone")} placeholder="+16145037888" /></Field>
            <Field label="Admin Notification Phone" hint="Your personal number that receives admin alerts"><input style={inputStyle} value={form.admin_notify_phone} onChange={set("admin_notify_phone")} placeholder="+16145476096" /></Field>
            <Field label="AI SMS Tone">
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {AI_TONES.map(t => (
                  <button key={t} type="button" onClick={() => setForm(f => ({ ...f, sms_tone: t }))}
                    style={{ padding: "9px 14px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", border: "1.5px solid", transition: "all 0.15s",
                      borderColor: form.sms_tone === t ? "#0891B2" : "#CBD5E1",
                      background: form.sms_tone === t ? "#EFF6FF" : "#fff",
                      color: form.sms_tone === t ? "#0891B2" : "#475569",
                    }}>{t}</button>
                ))}
              </div>
            </Field>
          </div>

          {/* ── Section 8: Payments (Stripe) ── */}
          <div style={sectionStyle}>
            <h2 style={{ fontSize: 16, fontWeight: 900, color: "#0891B2", margin: "0 0 4px", fontFamily: "inherit", textTransform: "none", letterSpacing: "normal" }}>8. Payments (Stripe)</h2>
            <p style={{ fontSize: 12, color: "#64748B", marginBottom: 14 }}>Your Stripe live keys. If you don't have Stripe yet, leave blank — we'll help you set it up.</p>
            <Field label="Stripe Live Publishable Key" hint="Starts with pk_live_..."><input style={inputStyle} value={form.stripe_publishable} onChange={set("stripe_publishable")} placeholder="pk_live_..." /></Field>
            <Field label="Stripe Live Secret Key" hint="Starts with sk_live_ — keep this private"><input style={{ ...inputStyle, fontFamily: "monospace" }} type="password" value={form.stripe_secret} onChange={set("stripe_secret")} placeholder="sk_live_..." /></Field>
          </div>

          {/* ── Section 9: Social Media ── */}
          <div style={sectionStyle}>
            <h2 style={{ fontSize: 16, fontWeight: 900, color: "#0891B2", margin: "0 0 16px", fontFamily: "inherit", textTransform: "none", letterSpacing: "normal" }}>9. Social Media</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Instagram Handle"><input style={inputStyle} value={form.instagram} onChange={set("instagram")} placeholder="@yourgym" /></Field>
              <Field label="TikTok Handle (optional)"><input style={inputStyle} value={form.tiktok} onChange={set("tiktok")} placeholder="@yourgym" /></Field>
            </div>
            <Field label="Facebook Page URL"><input style={inputStyle} value={form.facebook} onChange={set("facebook")} placeholder="https://facebook.com/yourgym" /></Field>
            <Field label="Google My Business URL (optional)" hint="For the reviews section"><input style={inputStyle} value={form.google_biz} onChange={set("google_biz")} placeholder="https://g.page/yourgym" /></Field>
          </div>

          {/* ── Section 10: Anything Else ── */}
          <div style={sectionStyle}>
            <h2 style={{ fontSize: 16, fontWeight: 900, color: "#0891B2", margin: "0 0 8px", fontFamily: "inherit", textTransform: "none", letterSpacing: "normal" }}>10. Anything Else</h2>
            <p style={{ fontSize: 12, color: "#64748B", marginBottom: 12 }}>Special programs, gym culture notes, anything the AI should know to represent your gym correctly.</p>
            <textarea style={{ ...inputStyle, resize: "vertical" }} rows={5} value={form.extra_notes} onChange={set("extra_notes")} placeholder="e.g. We have a women-only class on Tuesday mornings. Our gym has a no-ego policy. We do annual member transformation contests..." />
          </div>

          {error && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#DC2626", marginBottom: 16 }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={submitting}
            style={{ width: "100%", background: submitting ? "#A5F3FC" : "#0891B2", color: "#fff", border: "none", borderRadius: 10, padding: "16px 0", fontSize: 16, fontWeight: 900, cursor: submitting ? "default" : "pointer", letterSpacing: 0.3, fontFamily: "inherit" }}>
            {submitting ? "Submitting…" : "Submit Branding Questionnaire →"}
          </button>
          <p style={{ fontSize: 11, color: "#94A3B8", textAlign: "center", marginTop: 10 }}>
            Your information is private and only shared with GymPartnerAI.
          </p>
        </form>
      </div>
    </div>
  );
}
